import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import React from 'react';
import {
  acknowledgeBackgroundAssessment,
  addBackgroundAssessmentLog,
  getBackgroundAssessmentLog,
  resolveBackgroundAssessment,
} from '../redux';
import { getUser } from '../../user';
import {
  ConfirmChangesModal,
  reduceServerErrors,
  SaveChangesToast,
  UiToastAdapter,
} from '../../ui';
import { ComplianceLogModal } from './ComplianceLogModal';

/**
 * If confirmChanges is true:
 *  1. unsavedLogs and setUnsavedLogs are required
 *  2. Use submitting to show the save toast
 */
const InnerComplianceActions = ({
  confirmChanges,
  submitting,
  user,
  unsavedLogs,
  setUnsavedLogs,
  backgroundAssessmentLog,
  acknowledgingId,
  resolvingId,
  addingUserComment,
  onAcknowledge,
  onResolve,
  onAddUserComment,
  addBackgroundAssessmentLog,
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
}) => {
  const [confirmChangesModal, setConfirmChangesModal] = React.useState(false);
  const toastContainerClassName = classNames('bottom-toast-container', {
    'is-condensed': false,
  });

  const showModal = React.useCallback(() => {
    setConfirmChangesModal(true);
  }, [setConfirmChangesModal]);

  const hideModal = React.useCallback(() => {
    setConfirmChangesModal(false);
  }, [setConfirmChangesModal]);

  const addUnsavedLog = ({
    backgroundAssessmentId,
    backgroundAssessmentLogType,
    details,
  }) =>
    setUnsavedLogs([
      ...unsavedLogs,
      {
        backgroundAssessmentId,
        originator: ''.concat(user.firstName, ' ', user.lastName),
        backgroundAssessmentLogType,
        details,
      },
    ]);

  const submitLog = async ({
    backgroundAssessmentId,
    backgroundAssessmentLogType,
    details,
  }) => {
    const originator = ''.concat(user.firstName, ' ', user.lastName);
    if (
      backgroundAssessmentLogType.id ===
      backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType
    ) {
      await acknowledgeBackgroundAssessment({
        backgroundAssessmentId,
      });
    } else if (
      backgroundAssessmentLogType.id ===
      backgroundAssessmentLog.resolvedBackgroundAssessmentLogType
    ) {
      await resolveBackgroundAssessment({
        backgroundAssessmentId,
      });
    }
    await addBackgroundAssessmentLog({
      backgroundAssessmentId,
      type: backgroundAssessmentLogType.id,
      details,
      originator,
    });
    let result = true;
    if (backgroundAssessmentLog.isError()) {
      result = false;
      reduceServerErrors(backgroundAssessmentLog);
    } else {
      toast(UiToastAdapter('Log Added', 'success'));
    }
    return result;
  };

  const sendUpdates = async () => {
    const ackLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType
    );
    const resLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.resolvedBackgroundAssessmentLogType
    );
    const commentLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.userCommentBackgroundAssessmentLogType
    );
    if (ackLog) {
      await submitLog(ackLog);
    }
    if (resLog) {
      await submitLog(resLog);
    }
    if (commentLog) {
      await submitLog(commentLog);
    }
    setUnsavedLogs([]);
    hideModal();
    toast(UiToastAdapter('Updated', 'success'));
  };

  const handleAcknowledge = React.useCallback(
    values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      f({
        backgroundAssessmentId: acknowledgingId,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      onAcknowledge();
    },
    [confirmChanges, addUnsavedLog, submitLog, onAcknowledge]
  );

  const handleResolve = React.useCallback(
    values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      f({
        backgroundAssessmentId: resolvingId,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.resolvedBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      onResolve();
    },
    [confirmChanges, resolvingId, backgroundAssessmentLog]
  );

  const handleAddLog = React.useCallback(
    values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      f({
        backgroundAssessmentId: addingUserComment,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.userCommentBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      onAddUserComment();
    },
    [confirmChanges, addingUserComment, backgroundAssessmentLog]
  );

  return (
    <>
      <ComplianceLogModal
        isActive={acknowledgingId !== null}
        action="Acknowledge"
        onCancel={() => onAcknowledge(false)}
        onSubmit={handleAcknowledge}
      />
      <ComplianceLogModal
        isActive={resolvingId !== null}
        action="Resolve"
        onCancel={() => onResolve(false)}
        onSubmit={handleResolve}
      />
      <ComplianceLogModal
        isActive={addingUserComment !== null}
        action="Add Log"
        onCancel={() => onAddUserComment(false)}
        onSubmit={handleAddLog}
      />
      <div className={toastContainerClassName}>
        <SaveChangesToast
          message="Click the button to the right to save your changes"
          isLoading={submitting}
          isDisabled={submitting}
          isVisible={unsavedLogs.length > 0}
          onClickButton={showModal}
        />
      </div>
      <ConfirmChangesModal
        title="Background Assessment"
        isActive={confirmChangesModal}
        onClickCancel={hideModal}
        onClickSubmit={sendUpdates}
      />
    </>
  );
};

InnerComplianceActions.propTypes = {
  confirmChanges: PropTypes.bool,
  unsavedLogs: PropTypes.array,
  setUnsavedLogs: PropTypes.func,
  submitting: PropTypes.bool,
  user: PropTypes.object.isRequired,
  backgroundAssessmentLog: PropTypes.object.isRequired,
  acknowledgingId: PropTypes.number,
  resolvingId: PropTypes.number,
  addingUserComment: PropTypes.number,
  onAcknowledge: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onAddUserComment: PropTypes.func.isRequired,
  addBackgroundAssessmentLog: PropTypes.func.isRequired,
  acknowledgeBackgroundAssessment: PropTypes.func.isRequired,
  resolveBackgroundAssessment: PropTypes.func.isRequired,
};

InnerComplianceActions.defaultProps = {
  confirmChanges: false,
  unsavedLogs: [],
  setUnsavedLogs: () => {},
  submitting: null,
  acknowledgingId: null,
  resolvingId: null,
  addingUserComment: null,
};

const mapStateToProps = state => ({
  user: getUser(state),
  backgroundAssessmentLog: getBackgroundAssessmentLog(state),
});

export const ComplianceActions = connect(mapStateToProps, {
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
  addBackgroundAssessmentLog,
})(InnerComplianceActions);
